import { collection, doc, getDoc, setDoc, updateDoc, getDocs, query, where, deleteField } from 'firebase/firestore';
import { db } from './app';
import { VenuePaymentDetails } from '../../../../shared/models';

// Collection reference
const VENUE_PAYMENT_DETAILS_COLLECTION = 'venue_payment_details';

/**
 * Get payment details for a specific venue
 */
export const getVenuePaymentDetails = async (venueId: string): Promise<VenuePaymentDetails | null> => {
  console.log(`📋 Retrieving payment details for venue ${venueId}`);
  try {
    const docRef = doc(db, VENUE_PAYMENT_DETAILS_COLLECTION, venueId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data() as VenuePaymentDetails;
      // backwards compatibility for StripeConnectAccountID
      if (data.StripeConnectAccountID) {
        data.StripeConnectAccountId = data.StripeConnectAccountID;
        delete data.StripeConnectAccountID;
      }
      console.log(`✅ Found payment details for venue ${venueId}:`, data);
      // Log specifically if we have a Stripe Connect account ID
      console.log(`🔍 Stripe Connect Account ID: ${data.StripeConnectAccountId || 'Not connected'}`);
      return data;
    } else {
      console.log(`⚠️ No payment details document found for venue ${venueId}`);
      return null;
    }
  } catch (error) {
    console.error(`❌ Error getting venue payment details:`, error);
    return null;
  }
};

/**
 * Save payment details for a venue
 */
export const saveVenuePaymentDetails = async (details: VenuePaymentDetails): Promise<boolean> => {
  console.log(`💾 Saving payment details for venue ${details.VenueID}:`, details);
  try {
    const docRef = doc(db, VENUE_PAYMENT_DETAILS_COLLECTION, details.VenueID);
    await setDoc(docRef, details);
    console.log(`✅ Successfully saved payment details for venue ${details.VenueID}`);
    return true;
  } catch (error) {
    console.error(`❌ Error saving venue payment details:`, error);
    return false;
  }
};

/**
 * Update specific fields in venue payment details
 */
export const updateVenuePaymentDetails = async (
  venueId: string, 
  updates: Partial<VenuePaymentDetails>
): Promise<boolean> => {
  try {
    const docRef = doc(db, VENUE_PAYMENT_DETAILS_COLLECTION, venueId);
    
    // Get current payment details to determine which fields to delete
    const currentDetails = await getVenuePaymentDetails(venueId);
    if (!currentDetails) {
      console.error('Cannot update non-existent venue payment details');
      return false;
    }
    
    // Create an update object that includes field deletions
    const updateObject: any = { ...updates };
    
    // Handle invoice type changes by removing irrelevant fields
    if (updates.CommissionRate !== undefined) {
      // If using percentage of spend, remove flat rate fields if they exist
      if (currentDetails.FlatRateAmount !== undefined) updateObject.FlatRateAmount = deleteField();
      if (currentDetails.FlatRatePerPerson !== undefined) updateObject.FlatRatePerPerson = deleteField();
      if (currentDetails.FlatRatePerPersonPerHour !== undefined) updateObject.FlatRatePerPersonPerHour = deleteField();
    } else if (updates.FlatRateAmount !== undefined) {
      // If using flat rate, remove other fields if they exist
      if (currentDetails.CommissionRate !== undefined) updateObject.CommissionRate = deleteField();
      if (currentDetails.FlatRatePerPerson !== undefined) updateObject.FlatRatePerPerson = deleteField();
      if (currentDetails.FlatRatePerPersonPerHour !== undefined) updateObject.FlatRatePerPersonPerHour = deleteField();
    } else if (updates.FlatRatePerPerson !== undefined) {
      // If using flat rate per person, remove other fields if they exist
      if (currentDetails.CommissionRate !== undefined) updateObject.CommissionRate = deleteField();
      if (currentDetails.FlatRateAmount !== undefined) updateObject.FlatRateAmount = deleteField();
      if (currentDetails.FlatRatePerPersonPerHour !== undefined) updateObject.FlatRatePerPersonPerHour = deleteField();
    } else if (updates.FlatRatePerPersonPerHour !== undefined) {
      // If using flat rate per person per hour, remove other fields if they exist
      if (currentDetails.CommissionRate !== undefined) updateObject.CommissionRate = deleteField();
      if (currentDetails.FlatRateAmount !== undefined) updateObject.FlatRateAmount = deleteField();
      if (currentDetails.FlatRatePerPerson !== undefined) updateObject.FlatRatePerPerson = deleteField();
    }
    
    await updateDoc(docRef, updateObject);
    return true;
  } catch (error) {
    console.error('Error updating venue payment details:', error);
    return false;
  }
};

/**
 * Update section-specific payment details
 */
export const updateSectionPaymentDetails = async (
  venueId: string,
  sectionId: string,
  sectionDetails: {
    InvoiceType: 'percentage_of_spend' | 'flat_rate_per_person' | 'flat_rate' | 'flat_rate_per_person_per_hour';
    CommissionRate?: number;
    FlatRateAmount?: number;
    FlatRatePerPerson?: number;
    FlatRatePerPersonPerHour?: number;
  }
): Promise<boolean> => {
  try {
    const docRef = doc(db, VENUE_PAYMENT_DETAILS_COLLECTION, venueId);
    const path = `SectionPaymentDetails.${sectionId}`;
    
    // Get current payment details to determine the existing section config
    const currentDetails = await getVenuePaymentDetails(venueId);
    const currentSectionDetails = currentDetails?.SectionPaymentDetails?.[sectionId];
    
    // Create a properly formatted section update
    const updatedSection = { ...sectionDetails };
    
    // Remove fields that should not be present based on the invoice type
    if (sectionDetails.InvoiceType === 'percentage_of_spend') {
      // Only keep CommissionRate, remove others if they exist
      if (currentSectionDetails?.FlatRateAmount !== undefined) {
        await updateDoc(docRef, {
          [`SectionPaymentDetails.${sectionId}.FlatRateAmount`]: deleteField()
        });
      }
      if (currentSectionDetails?.FlatRatePerPerson !== undefined) {
        await updateDoc(docRef, {
          [`SectionPaymentDetails.${sectionId}.FlatRatePerPerson`]: deleteField()
        });
      }
      if (currentSectionDetails?.FlatRatePerPersonPerHour !== undefined) {
        await updateDoc(docRef, {
          [`SectionPaymentDetails.${sectionId}.FlatRatePerPersonPerHour`]: deleteField()
        });
      }
    } else if (sectionDetails.InvoiceType === 'flat_rate') {
      // Only keep FlatRateAmount, remove others if they exist
      if (currentSectionDetails?.CommissionRate !== undefined) {
        await updateDoc(docRef, {
          [`SectionPaymentDetails.${sectionId}.CommissionRate`]: deleteField()
        });
      }
      if (currentSectionDetails?.FlatRatePerPerson !== undefined) {
        await updateDoc(docRef, {
          [`SectionPaymentDetails.${sectionId}.FlatRatePerPerson`]: deleteField()
        });
      }
      if (currentSectionDetails?.FlatRatePerPersonPerHour !== undefined) {
        await updateDoc(docRef, {
          [`SectionPaymentDetails.${sectionId}.FlatRatePerPersonPerHour`]: deleteField()
        });
      }
    } else if (sectionDetails.InvoiceType === 'flat_rate_per_person') {
      // Only keep FlatRatePerPerson, remove others if they exist
      if (currentSectionDetails?.CommissionRate !== undefined) {
        await updateDoc(docRef, {
          [`SectionPaymentDetails.${sectionId}.CommissionRate`]: deleteField()
        });
      }
      if (currentSectionDetails?.FlatRateAmount !== undefined) {
        await updateDoc(docRef, {
          [`SectionPaymentDetails.${sectionId}.FlatRateAmount`]: deleteField()
        });
      }
      if (currentSectionDetails?.FlatRatePerPersonPerHour !== undefined) {
        await updateDoc(docRef, {
          [`SectionPaymentDetails.${sectionId}.FlatRatePerPersonPerHour`]: deleteField()
        });
      }
    } else if (sectionDetails.InvoiceType === 'flat_rate_per_person_per_hour') {
      // Only keep FlatRatePerPersonPerHour, remove others if they exist
      if (currentSectionDetails?.CommissionRate !== undefined) {
        await updateDoc(docRef, {
          [`SectionPaymentDetails.${sectionId}.CommissionRate`]: deleteField()
        });
      }
      if (currentSectionDetails?.FlatRateAmount !== undefined) {
        await updateDoc(docRef, {
          [`SectionPaymentDetails.${sectionId}.FlatRateAmount`]: deleteField()
        });
      }
      if (currentSectionDetails?.FlatRatePerPerson !== undefined) {
        await updateDoc(docRef, {
          [`SectionPaymentDetails.${sectionId}.FlatRatePerPerson`]: deleteField()
        });
      }
    }
    
    // Update with the new values
    await updateDoc(docRef, {
      [path]: updatedSection
    });
    
    return true;
  } catch (error) {
    console.error('Error updating section payment details:', error);
    return false;
  }
}; 